import React from 'react';
import styled, { keyframes } from 'styled-components';

/* ── Theme ─────────────────────────────────────────────────── */
const GOLD       = '#C9952A';
const GOLD_LIGHT = '#E8B84B';
const GOLD_DARK  = '#9A6F1A';
const CREAM      = '#FAF6ED';
const WARM_WHITE = '#FFFCF5';
const DARK       = '#0A1520';
const TEXT       = '#2A1F0E';
const TEXT_MID   = '#6B5540';

const float = keyframes`
  0%   { transform: translateY(0px); }
  50%  { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

const shimmer = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

/* ── Root ───────────────────────────────────────────────────── */
const PageContainer = styled.div`
  background: ${CREAM};
  overflow: hidden;
`;

/* ── Hero ───────────────────────────────────────────────────── */
const Hero = styled.section`
  position: relative;
  min-height: 72vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 2rem 8rem;
  background: linear-gradient(135deg, #0A1520 0%, #0E1C28 55%, #0A1520 100%);
  overflow: hidden;
  clip-path: polygon(0 0, 100% 0, 100% 88%, 0 100%);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse at 50% -10%, rgba(201,149,42,0.28) 0%, transparent 55%),
      radial-gradient(ellipse at 5%  90%, rgba(201,149,42,0.10) 0%, transparent 40%),
      radial-gradient(ellipse at 95% 90%, rgba(201,149,42,0.10) 0%, transparent 40%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 10 C30 0%2C70 0%2C100 10 C70 20%2C30 20%2C0 10' fill='none' stroke='rgba(201%2C149%2C42%2C0.07)' stroke-width='1.5'/%3E%3C/svg%3E");
    background-size: 100px 20px;
    opacity: 0.8;
    pointer-events: none;
    z-index: 0;
  }
`;

const HeroContent = styled.div`
  max-width: 860px;
  text-align: center;
  position: relative;
  z-index: 1;
  color: white;
`;

const Sanskrit = styled.h2`
  font-size: 2.2rem;
  color: rgba(232,184,75,0.65);
  margin-bottom: 0.6rem;
  font-family: 'Playfair Display', serif;
  font-weight: 400;
  font-style: italic;
`;

const HeroTitle = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: clamp(3rem, 6vw, 4.8rem);
  font-weight: 900;
  margin-bottom: 0.6rem;
  line-height: 1.05;
  background: linear-gradient(135deg, #fff 0%, ${GOLD_LIGHT} 50%, #fff 100%);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmer} 6s ease infinite, ${float} 7s ease-in-out infinite;
`;

const HeroSub = styled.h3`
  font-size: 1.15rem;
  color: rgba(255,255,255,0.65);
  font-weight: 400;
  font-family: 'Source Serif 4', serif;
  margin-top: 0.8rem;
`;

const GoldRule = styled.div`
  width: 60px;
  height: 1.5px;
  background: linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT});
  margin: 1.4rem auto;
  border-radius: 2px;
`;

const HeroButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  background: linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT});
  color: ${DARK};
  text-decoration: none;
  padding: 0.9rem 2.2rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Source Serif 4', serif;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(201,149,42,0.4);
  margin-top: 1.8rem;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 32px rgba(201,149,42,0.55);
  }

  svg { width: 18px; height: 18px; transition: transform 0.3s ease; }
  &:hover svg { transform: translateX(4px); }
`;

/* ── Section shell ──────────────────────────────────────────── */
const MainContent = styled.div`
  max-width: 1100px;
  margin: -4rem auto 0;
  padding: 0 2rem 5rem;
  position: relative;
  z-index: 2;
`;

const Section = styled.section`
  background: ${WARM_WHITE};
  border-radius: 20px;
  padding: 2.8rem;
  margin-bottom: 2rem;
  box-shadow: 0 8px 28px rgba(201,149,42,0.09);
  border: 1px solid rgba(201,149,42,0.12);
`;

const SectionTitle = styled.h2`
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.6rem, 2.5vw, 2.1rem);
  color: ${TEXT};
  margin-bottom: 1.8rem;
  text-align: center;
  position: relative;

  &::after {
    content: '';
    display: block;
    width: 52px;
    height: 2px;
    background: linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT});
    border-radius: 2px;
    margin: 0.75rem auto 0;
  }
`;

const Text = styled.p`
  font-size: 1rem;
  line-height: 1.85;
  color: ${TEXT_MID};
  margin-bottom: 1.2rem;
  font-family: 'Source Serif 4', serif;
`;

/* ── Icon helper ────────────────────────────────────────────── */
const IconWrap = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(201,149,42,0.1), rgba(232,184,75,0.06));
  border: 1.5px solid rgba(201,149,42,0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  flex-shrink: 0;
`;

const GoldIcon = ({ children }) => (
  <IconWrap>
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none"
         stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  </IconWrap>
);

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.4rem;
  margin-bottom: 2rem;
`;

const ServiceCard = styled.div`
  background: ${WARM_WHITE};
  border-radius: 18px;
  padding: 2rem;
  box-shadow: 0 6px 20px rgba(201,149,42,0.09);
  border-top: 3px solid ${GOLD};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 14px 36px rgba(201,149,42,0.16);
  }

  h3 {
    font-family: 'Playfair Display', serif;
    font-size: 1.2rem;
    color: ${TEXT};
    margin-bottom: 0.6rem;
  }
`;

const StatNumber = styled.div`
  font-family: 'Playfair Display', serif;
  font-size: 2.2rem;
  font-weight: 800;
  background: linear-gradient(135deg, ${GOLD_DARK}, ${GOLD});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.4rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;

  li {
    background: ${CREAM};
    padding: 1.8rem;
    border-radius: 14px;
    border-left: 3px solid ${GOLD};
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(201,149,42,0.12);
    }
  }
`;

const Highlight = styled.span`
  color: ${GOLD_DARK};
  font-weight: 700;
  font-family: 'Playfair Display', serif;
`;

const CTASection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  margin-top: 2rem;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT});
  color: ${DARK};
  text-decoration: none;
  padding: 0.9rem 2rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Source Serif 4', serif;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(201,149,42,0.35);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(201,149,42,0.5);
  }
`;

/* ── App section styled components ─────────────────────────── */
const PhoneShowcase = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 1.5rem;
  margin: 2.5rem 0;
  flex-wrap: wrap;
`;

const PhoneWrap = styled.div`
  position: relative;
  transform: ${({ $center }) => $center ? 'scale(1.08)' : 'scale(1)'};
  transition: transform 0.3s ease;
  z-index: ${({ $center }) => $center ? 2 : 1};

  &:hover { transform: ${({ $center }) => $center ? 'scale(1.11)' : 'scale(1.03)'}; }
`;

const PhoneFrame = styled.div`
  width: 180px;
  height: 360px;
  border-radius: 28px;
  border: 9px solid ${TEXT};
  overflow: hidden;
  box-shadow:
    0 24px 56px rgba(0,0,0,0.35),
    0 0 0 1.5px rgba(201,149,42,0.25),
    inset 0 0 0 1px rgba(255,255,255,0.06);
  background: #111;
  position: relative;

  /* notch */
  &::before {
    content: '';
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    width: 48px;
    height: 6px;
    background: ${TEXT};
    border-radius: 4px;
    z-index: 3;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 600px) {
    width: 140px;
    height: 280px;
  }
`;

const DownloadRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 2.5rem;
`;

const PlayStoreBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.9rem;
  background: ${TEXT};
  color: #fff;
  text-decoration: none;
  padding: 0.75rem 1.6rem;
  border-radius: 14px;
  border: 1.5px solid rgba(201,149,42,0.35);
  transition: all 0.3s ease;
  min-width: 200px;

  &:hover {
    border-color: ${GOLD};
    background: #3A2810;
    box-shadow: 0 8px 28px rgba(201,149,42,0.28);
    transform: translateY(-2px);
  }

  svg {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
  }
`;

const PlayText = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.15;

  small {
    font-size: 0.62rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.65);
  }

  strong {
    font-size: 1rem;
    font-family: 'Source Serif 4', serif;
    color: #fff;
  }
`;

const AppBadge = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT});
  color: ${TEXT};
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.2rem 0.65rem;
  border-radius: 20px;
  margin-left: 0.75rem;
  vertical-align: middle;
`;

const Dhārā = () => (
  <PageContainer>
    <Hero>
      <HeroContent>
        <Sanskrit>धारा</Sanskrit>
        <HeroTitle>Dhārā</HeroTitle>
        <GoldRule />
        <HeroSub>Data API Service &amp; Example Apps</HeroSub>
        <HeroButton href="https://dhara.bheri.in" target="_blank" rel="noopener noreferrer">
          Explore Webapp
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </HeroButton>
      </HeroContent>
    </Hero>

    <MainContent>
      <ServiceGrid>
        <ServiceCard>
          <GoldIcon>
            {/* Open book — Dictionary */}
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
            <line x1="9" y1="7" x2="15" y2="7"/><line x1="9" y1="11" x2="15" y2="11"/>
          </GoldIcon>
          <StatNumber>3.5L+</StatNumber>
          <h3>Dictionary</h3>
          <Text>Comprehensive collection of Indic words with detailed definitions from authentic sources.</Text>
        </ServiceCard>
        <ServiceCard>
          <GoldIcon>
            {/* Server stack — Page Server */}
            <rect x="2" y="2" width="20" height="8" rx="2"/>
            <rect x="2" y="14" width="20" height="8" rx="2"/>
            <line x1="6" y1="6" x2="6.01" y2="6" strokeWidth="3"/>
            <line x1="6" y1="18" x2="6.01" y2="18" strokeWidth="3"/>
            <line x1="10" y1="6" x2="16" y2="6"/>
            <line x1="10" y1="18" x2="16" y2="18"/>
          </GoldIcon>
          <h3>Page Server</h3>
          <Text>High-performance content delivery system optimized for Indic knowledge resources.</Text>
        </ServiceCard>
        <ServiceCard>
          <GoldIcon>
            {/* Shield with check — Authentic References */}
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            <polyline points="9 12 11 14 15 10"/>
          </GoldIcon>
          <h3>Authentic References</h3>
          <Text>Every piece of information comes with proper citations and source validation.</Text>
        </ServiceCard>
      </ServiceGrid>

      {/* ── Android App ──────────────────────────────────── */}
      <Section>
        <SectionTitle>
          Available on Android
          <AppBadge>Free</AppBadge>
        </SectionTitle>
        <p style={{ textAlign: 'center', color: TEXT_MID, fontFamily: "'Source Serif 4', serif", fontSize: '1rem', lineHeight: 1.8, maxWidth: '560px', margin: '0 auto' }}>
          Explore Sanskrit dictionaries, original verses, and verified heritage references on the go.
          5,000+ downloads and counting.
        </p>

        <PhoneShowcase>
          <PhoneWrap>
            <PhoneFrame>
              <img src="/dharaapp1.jpg" alt="Dhara app screenshot 1" loading="lazy" />
            </PhoneFrame>
          </PhoneWrap>
          <PhoneWrap $center>
            <PhoneFrame>
              <img src="/dharaapp2.jpg" alt="Dhara app screenshot 2" loading="lazy" />
            </PhoneFrame>
          </PhoneWrap>
          <PhoneWrap>
            <PhoneFrame>
              <img src="/dharaapp3.jpg" alt="Dhara app screenshot 3" loading="lazy" />
            </PhoneFrame>
          </PhoneWrap>
        </PhoneShowcase>

        <DownloadRow>
          <PlayStoreBtn
            href="https://play.google.com/store/apps/details?id=in.bheri.dhara"
            target="_blank"
            rel="noopener noreferrer"
          >
            {/* Google Play icon */}
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.18 23.76a2.02 2.02 0 01-1.06-1.8V2.04C2.12 1.4 2.5.86 3.18.24L13.42 12 3.18 23.76z" fill="#EA4335"/>
              <path d="M17.18 8.18L5.24.86 3.18.24 13.42 12l3.76-3.82z" fill="#FBBC05"/>
              <path d="M17.18 15.82L13.42 12 3.18 23.76l2.06-.62 11.94-7.32z" fill="#34A853"/>
              <path d="M21.7 10.46l-4.52-2.28L13.42 12l3.76 3.82 4.54-2.3c1.3-.66 1.3-2.4-.02-3.06z" fill="#4285F4"/>
            </svg>
            <PlayText>
              <small>Get it on</small>
              <strong>Google Play</strong>
            </PlayText>
          </PlayStoreBtn>
        </DownloadRow>
      </Section>

      <Section>
        <SectionTitle>What Makes Dhārā Unique?</SectionTitle>
        <FeatureList>
          <li>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', marginBottom: '0.5rem' }}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
              <Highlight>Lightning Fast Results</Highlight>
            </div>
            <Text>Get instant answers to your queries with our optimized search engine.</Text>
          </li>
          <li>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', marginBottom: '0.5rem' }}>
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <polyline points="9 12 11 14 15 10"/>
              </svg>
              <Highlight>Authentic References</Highlight>
            </div>
            <Text>Every piece of information comes with proper source validation and citations.</Text>
          </li>
        </FeatureList>
      </Section>

      <Section>
        <SectionTitle>Who Can Benefit?</SectionTitle>
        <FeatureList>
          <li>
            <Text><Highlight>IKS Heritage Researchers</Highlight><br />Access comprehensive dictionary services and reference tools for efficient research.</Text>
          </li>
          <li>
            <Text><Highlight>Heritage Enterprises</Highlight><br />Available as example apps and APIs to supercharge your enterprise solutions.</Text>
          </li>
          <li>
            <Text>
              <Highlight>Everyone Else</Highlight><br />
              Search for heritage information · Discover mythological characters · Plan heritage visits
            </Text>
          </li>
        </FeatureList>
      </Section>

      <Section>
        <SectionTitle>Why Choose Dhārā?</SectionTitle>
        <Text>
          Unlike conventional search engines, Dhārā specializes in Indic content with a focus on
          authenticity and reliability. Every search result comes with verified references, making
          it the most trusted platform for Indic knowledge exploration.
        </Text>
        <CTASection>
          <CTAButton href="https://dhara.bheri.in" target="_blank" rel="noopener noreferrer">
            📚 Dhara Webapp
          </CTAButton>
        </CTASection>
      </Section>
    </MainContent>
  </PageContainer>
);

export default Dhārā;
